<script setup lang="ts">
// DUPLICATED/adapted from packages/frontend/src/modules/browser/components/rules/RuleSection.vue
// for the studio preview. This is a DISPLAY-ONLY SUBSET: favorite stars,
// FAQ content, router links, ContentSection collapse/expand, and search query were
// dropped. Section names are plain headings; containers expand inline. Keep the
// container dispatch logic (Battle Tactics Card → BattleTacticCard, missionType →
// BattleplanCard, else → RuleComponentView per component) in sync with the frontend
// original; do not re-add app-specific chrome here.
//
// Import rewiring vs original:
//   - BattleTacticCard → ../BattleTacticCard.vue (local display-only editor copy)
//   - BattleplanCard → ../BattleplanCard.vue (local editor copy)
//   - RuleComponent → ./RuleComponentView.vue (local editor copy)
//   - cardFromContainer / battleplanFromContainer → copied inline (same logic)
//   - useFavorite, ContentSection → removed
import {
  BattleTacticType,
  OtherSuperType,
  OtherTypes,
  fromAoSData,
  isMpBattlePack,
  isPtgBattlePack,
  type IBattleTacticCard,
  type IBattleplan,
  type IBattleplanContentSection,
  type BattlePack,
  type RuleContainer,
  type RuleSection,
} from '@scrolldex/shared';
import { computed } from 'vue';
import BattleTacticCard from '../BattleTacticCard.vue';
import BattleplanCard from '../BattleplanCard.vue';
import RuleComponentView from './RuleComponentView.vue';

const props = defineProps<{
  section: RuleSection;
}>();

const sectionBattlePack = computed<BattlePack | undefined>(() => {
  const bp = props.section.battlePack;
  return bp && (isMpBattlePack(bp) || isPtgBattlePack(bp)) ? bp : undefined;
});

// Copied from packages/frontend/src/modules/browser/components/rules/RuleSection.vue.
// Reconstructs an IBattleTacticCard from a container whose name starts with
// "Battle Tactics Card". Keep in sync with the frontend original.
function cardFromContainer(container: RuleContainer): IBattleTacticCard {
  const name = container.name.split(' - ')[1]?.trim() || container.name;
  const affrayComponent = container.components.find(
    (c) => c.contentType === 'battleTactic' && c.title?.startsWith('Affray - '),
  );
  const strikeComponent = container.components.find(
    (c) => c.contentType === 'battleTactic' && c.title?.startsWith('Strike - '),
  );
  const dominationComponent = container.components.find(
    (c) => c.contentType === 'battleTactic' && c.title?.startsWith('Domination - '),
  );

  // first non affray/strike/domination text
  const text =
    container.components.find(
      (c) =>
        c.contentType === 'text' &&
        !c.text?.startsWith('Affray - ') &&
        !c.text?.startsWith('Strike - ') &&
        !c.text?.startsWith('Domination - '),
    )?.text || '';

  return {
    id: '',
    type: OtherTypes.BattleTacticCard,
    superType: OtherSuperType,
    name,
    text,
    affray: {
      type: BattleTacticType.Affray,
      name: affrayComponent?.title?.replace('Affray - ', '') || '',
      description: affrayComponent?.text || '',
    },
    strike: {
      type: BattleTacticType.Strike,
      name: strikeComponent?.title?.replace('Strike - ', '') || '',
      description: strikeComponent?.text || '',
    },
    domination: {
      type: BattleTacticType.Domination,
      name: dominationComponent?.title?.replace('Domination - ', '') || '',
      description: dominationComponent?.text || '',
    },
  };
}

// Copied from packages/frontend/src/modules/browser/components/rules/RuleSection.vue.
// Reconstructs an IBattleplan from a missionType container. Keep in sync with the
// frontend original.
function battleplanFromContainer(container: RuleContainer): IBattleplan {
  const abilities = container.components
    .filter((c) => c.contentType === 'ability' && c.ability)
    .map((c) => fromAoSData(c.ability!));

  const sections = sectionsFromContainer(container);
  const twist = container.components.find((c) => c.contentType === 'twist')?.text || undefined;
  const scoringText =
    container.components.find((c) => c.contentType === 'scoring')?.text || undefined;

  return {
    name: container.name,
    // The rules narrative only — twist, scoring and ability text are their own
    // fields/components, so excluding them keeps rulesText from double-rendering
    // the twist/scoring content the card already shows separately.
    rulesText: container.components
      .filter(
        (c) =>
          c.contentType !== 'twist' &&
          c.contentType !== 'scoring' &&
          c.contentType !== 'ability' &&
          c.contentType !== 'battleTactic',
      )
      .map((c) => c.text)
      .filter(Boolean)
      .join('\n\n'),
    components: [],
    abilities,
    twist,
    scoringText,
    scoringRules: [],
    sections: sections.length ? sections : undefined,
  };
}

function sectionsFromContainer(container: RuleContainer): IBattleplanContentSection[] {
  const sections: IBattleplanContentSection[] = [];
  let current: IBattleplanContentSection | null = null;
  let afterAbility = false;

  for (const component of container.components) {
    if (component.contentType === 'header') {
      current = { title: component.title || component.text || '' };
      sections.push(current);
      afterAbility = false;
      continue;
    }
    if (!current) continue;

    if (component.contentType === 'ability' && component.ability) {
      current.abilities = [...(current.abilities ?? []), fromAoSData(component.ability)];
      afterAbility = true;
      continue;
    }

    if (component.contentType === 'text' && component.text) {
      if (afterAbility) {
        current.trailingText = current.trailingText
          ? `${current.trailingText}\n\n${component.text}`
          : component.text;
      } else {
        current.text = current.text ? `${current.text}\n\n${component.text}` : component.text;
      }
    }
  }

  return sections.filter(
    (section) =>
      section.title.trim() ||
      section.text?.trim() ||
      section.trailingText?.trim() ||
      (section.abilities?.length ?? 0) > 0,
  );
}
</script>
<template>
  <div class="rule-section">
    <div class="section-name">{{ section.name }}</div>
    <RuleSectionView v-for="sub in section.subSections" :key="sub.name" :section="sub" />
    <div v-for="container in section.containers" :key="container.name">
      <template v-if="container.name.startsWith('Battle Tactics Card')">
        <BattleTacticCard :card="cardFromContainer(container)" />
      </template>
      <template v-else-if="container.containerType === 'missionType'">
        <BattleplanCard
          :battleplan="battleplanFromContainer(container)"
          :battle-pack="sectionBattlePack"
        />
      </template>
      <template v-else>
        <div class="container-name">{{ container.name }}</div>
        <div v-for="(component, idx) in container.components" :key="idx">
          <RuleComponentView :component="component" />
        </div>
      </template>
    </div>
  </div>
</template>
<style scoped>
/* Each section is its own raised card on the studio surface. */
.rule-section {
  margin: var(--st-space-3) 0;
  padding: var(--st-space-4);
  background: var(--st-surface);
  border: 1px solid var(--st-border);
  border-radius: var(--st-radius-md);
  box-shadow: var(--st-shadow-2);
}

/* Section heading — accent underline anchors the card. */
.section-name {
  font-size: var(--st-text-lg);
  font-weight: var(--st-weight-bold);
  color: var(--st-text);
  margin: 0 0 var(--st-space-3);
  padding-bottom: var(--st-space-2);
  border-bottom: 2px solid var(--st-accent);
}

.container-name {
  font-size: var(--st-text-md);
  font-weight: var(--st-weight-semibold);
  color: var(--st-text-muted);
  margin: var(--st-space-2) 0 var(--st-space-1);
}
</style>
